import React, { useState, useMemo, useEffect } from 'react';
import { TIconNames } from 'components/Icon';
import { SelectInfo } from 'rc-menu/lib/interface';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { changeSidebarState } from 'redux/slices';
import { FontWeights } from 'core/CssVariables';
import {
  PrivateRouteType,
  RoutesPaths,
  SidebarGroups,
} from 'routes/Routes.types';
import ToolTip from 'components/ToolTip/ToolTip';
import { Row } from 'components/Grid/Grid';
import { TFeatureFlags } from 'data/types/featureFlags.type';
import { HelpSupportModal } from 'components/HelpSupport';
import { ReleaseNotesModal } from 'components/ReleaseNotes';
import { FaqModal } from 'components/Faq';
import { useGetCurrentReleaseQuery } from 'redux/apiSlices/releases.slice';
import { ReactComponent as ZestLogo } from 'assets/images/zestLogo.svg';
import {
  SMenu,
  SMenuItemGroup,
  SSidebarLogo,
  SVersionBar,
  SVersionLink,
} from './Sidebar.style';
import { MenuItem, SubMenu } from './MenuItem/MenuItem';
import useGetPrivateRoutesByRole from '../../hooks/useGetPrivateRoutesByRole';
import CollapseIcons from './components/CollapseIcons';
import NavIcon from './NavIcon/NavIcon';
import NavLabel from './NavLabel/NavLabel';

/* Order the section headings appear in the panel. */
const groupOrder: SidebarGroups[] = [
  SidebarGroups.Work,
  SidebarGroups.Clients,
  SidebarGroups.Manage,
  SidebarGroups.Settings,
];

const Sidebar = () => {
  const [selected, setSelected] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [releaseNotesModalVisible, setReleaseNotesModalVisible] =
    useState(false);
  const [faqModalVisible, setFaqModalVisible] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sidebar = useAppSelector(state => state.appSettings.sidebar);
  const { data: currentRelease } = useGetCurrentReleaseQuery();
  const user = useAppSelector(state => state.authSlice.user);
  const privateRoutes: PrivateRouteType[] = [];
  const isAccessLimited = useAppSelector(
    state => state.paymentInfoSlice?.isAccessLimited,
  );
  useGetPrivateRoutesByRole().forEach(item => {
    if (!item.notInSidebar) {
      if (item.subRoutes?.length) {
        const newRoute = { ...item };
        const newSubRoutes = item.subRoutes.filter(
          subRoute => !subRoute.notInSidebar,
        );
        newRoute.subRoutes = newSubRoutes.filter(
          subRoute =>
            !subRoute.featureFlag ||
            user?.featureFlags[subRoute.featureFlag as keyof TFeatureFlags],
        );
        privateRoutes.push(newRoute);
      } else {
        privateRoutes.push(item);
      }
    }
  });

  const rootSubmenuKeys = useMemo(() => {
    const rootSubmenuKeysArr: string[] = [];
    privateRoutes.forEach(route => {
      if (route.subRoutes) rootSubmenuKeysArr.push(route.path);
    });
    return rootSubmenuKeysArr;
  }, []);

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    if (latestOpenKey && rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const onSelect = (selectInfo: SelectInfo) => {
    const { key, keyPath } = selectInfo;
    if (window.innerWidth <= 767) {
      dispatch(
        changeSidebarState({
          hidden: true,
        }),
      );
    }

    if (keyPath.length === 1) {
      setOpenKeys([]);
    }
    setSelected([key]);
  };

  const onLogoClick = () => {
    if (window.innerWidth <= 767) {
      dispatch(
        changeSidebarState({
          hidden: true,
        }),
      );
    }
    if (!isAccessLimited) {
      navigate(RoutesPaths.Workfeed);
    }
  };

  const onHelpClick = () => {
    setHelpModalVisible(true);
  };

  const onHelpModalClose = () => {
    setHelpModalVisible(false);
  };

  const onFaqClick = () => {
    setFaqModalVisible(true);
  };

  const onFaqModalClose = () => {
    setFaqModalVisible(false);
  };

  const onVersionClick = () => {
    setReleaseNotesModalVisible(true);
  };

  const onReleaseNotesModalClose = () => {
    setReleaseNotesModalVisible(false);
  };

  useEffect(() => {
    const paths = location.pathname
      .split('/')
      .filter(item => item && item !== 'app');
    const route = privateRoutes.find(item => item.path === paths[0]);
    if (
      paths.length > 1 &&
      rootSubmenuKeys.includes(paths[0]) &&
      route?.subRoutes?.length
    ) {
      setSelected([`${paths[0]}/${paths[1]}`]);
    } else {
      setSelected([paths[0]]);
    }
    setOpenKeys([paths[0]]);
  }, [sidebar.hidden, location.pathname]);

  const renderRoute = ({ title, icon, path, subRoutes }: PrivateRouteType) => {
    if (!subRoutes?.length) {
      return (
        <MenuItem key={path} title="">
          <ToolTip placement="right" title={sidebar.collapsed ? title : ''}>
            {icon && <NavIcon icon={icon} />}
            <NavLink to={`${path}`}>
              {sidebar.collapsed ? '' : <NavLabel text={title} />}
            </NavLink>
          </ToolTip>
        </MenuItem>
      );
    }
    return (
      <SubMenu
        title={sidebar.collapsed ? '' : <NavLabel text={title} />}
        key={path}
        icon={
          icon && (
            <ToolTip placement="right" title={sidebar.collapsed ? title : ''}>
              <NavIcon icon={icon} />
            </ToolTip>
          )
        }
      >
        {subRoutes.map(subRoute => (
          <MenuItem
            key={subRoute.path}
            icon={subRoute.icon && <NavIcon icon={subRoute.icon} />}
            title=""
          >
            <ToolTip
              placement="right"
              title={sidebar.collapsed ? subRoute.title : ''}
            >
              <NavLink to={`${subRoute.path}`}>
                {sidebar.collapsed ? '' : <NavLabel text={subRoute.title} />}
              </NavLink>
            </ToolTip>
          </MenuItem>
        ))}
      </SubMenu>
    );
  };

  /* Anything without a group still renders, above the grouped sections, so a
     new route can never silently disappear from the panel. */
  const ungroupedRoutes = privateRoutes.filter(route => !route.group);

  /* The design emphasises the first heading, so the panel has to know which one
     that is. A section with no visible routes is skipped, and the ungrouped
     routes above are sibling list items, so neither groupOrder[0] nor a CSS
     :first-of-type would land on it reliably. */
  const leadGroup = groupOrder.find(
    group =>
      group === SidebarGroups.Settings ||
      privateRoutes.some(route => route.group === group),
  );

  return (
    <>
      <SSidebarLogo collapsed={sidebar.collapsed}>
        {!sidebar.collapsed && (
          <ZestLogo className="icon-logo" onClick={onLogoClick} />
        )}
        <CollapseIcons />
      </SSidebarLogo>
      <SMenu
        onSelect={onSelect}
        openKeys={sidebar.hidden ? [] : openKeys}
        selectedKeys={selected}
        onOpenChange={onOpenChange}
        mode="inline"
        className={sidebar.collapsed ? 'is-collapsed' : ''}
      >
        {ungroupedRoutes.map(renderRoute)}

        {groupOrder.map(group => {
          const routes = privateRoutes.filter(route => route.group === group);
          const isSettings = group === SidebarGroups.Settings;
          if (!routes.length && !isSettings) return null;
          return (
            <SMenuItemGroup
              key={group}
              className={group === leadGroup ? 'is-lead-group' : undefined}
              title={sidebar.collapsed ? '' : group}
            >
              {routes.map(renderRoute)}
              {isSettings && [
                <MenuItem key="help" title="" onClick={onHelpClick}>
                  <ToolTip
                    placement="right"
                    title={sidebar.collapsed ? 'Help' : ''}
                  >
                    <NavIcon icon={TIconNames.INFO} />
                    {sidebar.collapsed ? '' : <NavLabel text="Help" />}
                  </ToolTip>
                </MenuItem>,
                <MenuItem key="faq" title="" onClick={onFaqClick}>
                  <ToolTip
                    placement="right"
                    title={sidebar.collapsed ? 'FAQ' : ''}
                  >
                    <NavIcon icon={TIconNames.FILE_DESCRIPTION} />
                    {sidebar.collapsed ? '' : <NavLabel text="FAQ" />}
                  </ToolTip>
                </MenuItem>,
              ]}
            </SMenuItemGroup>
          );
        })}
      </SMenu>
      {(currentRelease?.version || process.env.REACT_APP_VERSION) && (
        <SVersionBar>
          <Row align="middle" justify="start">
            <ToolTip
              placement="right"
              title={
                currentRelease ? (
                  <>
                    <Row>
                      <strong>v{currentRelease.version} Highlights</strong>
                    </Row>
                    {currentRelease.highlights?.map(
                      (highlight: string, index: number) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Row key={index}>• {highlight}</Row>
                      ),
                    )}
                    <Row style={{ marginTop: 4, fontSize: 11, opacity: 0.7 }}>
                      Click to view full release notes
                    </Row>
                  </>
                ) : (
                  ''
                )
              }
            >
              <SVersionLink
                onClick={currentRelease ? onVersionClick : undefined}
                fontlevel={6}
                fontWeight={FontWeights.Regular}
                style={{ cursor: currentRelease ? 'pointer' : 'default' }}
              >
                {sidebar.collapsed
                  ? ''
                  : `v${
                      currentRelease?.version || process.env.REACT_APP_VERSION
                    }`}
              </SVersionLink>
            </ToolTip>
          </Row>
        </SVersionBar>
      )}
      <HelpSupportModal visible={helpModalVisible} onClose={onHelpModalClose} />
      <FaqModal visible={faqModalVisible} onClose={onFaqModalClose} />
      {releaseNotesModalVisible && currentRelease && (
        <ReleaseNotesModal
          visible={releaseNotesModalVisible}
          onClose={onReleaseNotesModalClose}
          version={currentRelease.version}
        />
      )}
    </>
  );
};
export default Sidebar;
